import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { HttpClientService } from "./http-client.service";
import {
  CardCommissionDeposit,
  CardCommissionDepositApprovePayload,
  CardCommissionDepositBulkDepositPayload,
  CardCommissionDepositBulkDepositResult,
  CardCommissionDepositEvent,
  CardCommissionDepositListFilters,
  CardCommissionDepositListResult,
} from "../interfaces/card-commission-deposit/card-commission-deposit.interface";

@Injectable()
export class CardCommissionDepositsService {
  constructor(private http: HttpClientService) {}

  private buildParams(filters?: CardCommissionDepositListFilters): { [param: string]: string | string[] } {
    const params: { [param: string]: string | string[] } = {};
    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.user_registration) {
      params.user_registration = filters.user_registration;
    }
    if (filters?.application_id != null) {
      params.application_id = String(filters.application_id);
    }
    if (filters?.beneficiary_role) {
      params.beneficiary_role = filters.beneficiary_role;
    }
    if (filters?.assignee != null && filters.assignee !== "") {
      params.assignee = String(filters.assignee);
    }
    if (filters?.signature_state) {
      params.signature_state = filters.signature_state;
    }
    if (filters?.search != null && filters.search !== "") {
      params.search = filters.search;
    }
    if (filters?.page != null && filters.page > 0) {
      params.page = String(filters.page);
    }
    if (filters?.ep_ids?.length) {
      params["ep_ids[]"] = filters.ep_ids.map(String);
    }
    if (filters?.dealership_ids?.length) {
      params["dealership_ids[]"] = filters.dealership_ids.map(String);
    }
    if (filters?.disbursement_date_from) {
      params.disbursement_date_from = filters.disbursement_date_from;
    }
    if (filters?.disbursement_date_to) {
      params.disbursement_date_to = filters.disbursement_date_to;
    }
    if (filters?.sort_by) {
      params.sort_by = filters.sort_by;
    }
    if (filters?.sort_dir) {
      params.sort_dir = filters.sort_dir;
    }
    return params;
  }

  // GET /api/card_commission_deposits → { data, total_count }
  public list(filters?: CardCommissionDepositListFilters): Observable<CardCommissionDepositListResult> {
    const params = this.buildParams(filters);
    const options = Object.keys(params).length ? { params } : {};
    return this.http.get<CardCommissionDepositListResult>("/api/card_commission_deposits", options).pipe(
      map((response) => ({
        data: response?.data ?? [],
        total_count: response?.total_count ?? 0,
        counts: response?.counts,
        filters: response?.filters,
      })),
      tap((_) => console.log("CardCommissionDepositsService.list: Ok."))
    );
  }

  // Same endpoint as list() but requests the bucket counters in the response body
  // (GET /api/card_commission_deposits?...&include_counts=true -> { data, total_count, counts, filters }).
  public listWithCounts(filters?: CardCommissionDepositListFilters): Observable<CardCommissionDepositListResult> {
    const params = { ...this.buildParams(filters), include_counts: "true" };
    return this.http.get<CardCommissionDepositListResult>("/api/card_commission_deposits", { params }).pipe(
      map((response) => ({
        data: response?.data ?? [],
        total_count: response?.total_count ?? 0,
        counts: response?.counts,
        filters: response?.filters,
      })),
      tap((_) => console.log("CardCommissionDepositsService.listWithCounts: Ok."))
    );
  }

  // PUT /api/card_commission_deposits/:id
  // Triggers a state-machine event on the server (request/mark_deposited/mark_failed/reject).
  public transition(id: number, event: CardCommissionDepositEvent): Observable<CardCommissionDeposit> {
    return this.http
      .put(`/api/card_commission_deposits/${id}`, { event })
      .pipe(tap((_) => console.log("CardCommissionDepositsService.transition: Ok.")));
  }

  // PUT /api/card_commission_deposits/bulk_deposit
  // Marks deposits currently in "requested" as deposited. Ineligible or unknown ids are skipped
  // silently (so replays are safe), and a 200 can still report per-deposit failures.
  public bulkDeposit(
    payload: CardCommissionDepositBulkDepositPayload
  ): Observable<CardCommissionDepositBulkDepositResult> {
    return this.http.put("/api/card_commission_deposits/bulk_deposit", payload);
  }

  // POST /api/card_commission_deposits/:id/approvals
  public approveAuthorization(
    id: number,
    payload: CardCommissionDepositApprovePayload
  ): Observable<CardCommissionDeposit> {
    return this.http
      .post(`/api/card_commission_deposits/${id}/approvals`, payload)
      .pipe(tap((_) => console.log("CardCommissionDepositsService.approveAuthorization: Ok.")));
  }

  // POST /api/card_commission_deposits/:id/notifications
  // Resends the deposit notification email (no WhatsApp). Recipient is resolved on the server.
  public resendNotification(id: number): Observable<CardCommissionDeposit> {
    return this.http
      .post(`/api/card_commission_deposits/${id}/notifications`, {})
      .pipe(tap((_) => console.log("CardCommissionDepositsService.resendNotification: Ok.")));
  }

  // GET /api/card_commission_deposits/pending_xlsx
  public pendingExport(ids?: readonly number[]): Observable<Blob> {
    return this.downloadExport("/api/card_commission_deposits/pending_xlsx", ids, "pendingExport");
  }

  // GET /api/card_commission_deposits/unregistered_users_xlsx
  public unregisteredUsersExport(ids?: readonly number[]): Observable<Blob> {
    return this.downloadExport("/api/card_commission_deposits/unregistered_users_xlsx", ids, "unregisteredUsersExport");
  }

  // GET /api/card_commission_deposits/authorization_csv
  public authorizationExport(ids?: readonly number[]): Observable<Blob> {
    return this.downloadExport("/api/card_commission_deposits/authorization_csv", ids, "authorizationExport");
  }

  private downloadExport(path: string, ids: readonly number[] | undefined, logLabel: string): Observable<Blob> {
    const options: { responseType: "json"; params?: { "ids[]": string[] } } = {
      responseType: "blob" as "json",
    };
    if (ids?.length) {
      options.params = { "ids[]": ids.map(String) };
    }
    return this.http
      .get<Blob>(path, options)
      .pipe(tap((_) => console.log(`CardCommissionDepositsService.${logLabel}: Ok.`)));
  }
}
